import Channel, { ThreadChannel } from '@@types/Channel';
import { LOG_ID, REACTION_ID, StringTMap, THREAD_CHANNEL_ID } from '@@types/common';
import Log, { ReplyLog } from '@@types/Log';
import { Reaction } from '@@types/Log/Reaction';
import LogFile from '@@types/LogFile';

import ChannelLogsStore, { channelLogsStore } from '~/stores/ChannelLogsStore';
import { channelsStore } from '~/stores/ChannelsStore';
import { channelLogData } from '~/stores/data/ChannelLogData';
import { threadDetailUiState } from '~/stores/DetailUiState';
import { threadChannelStore } from '~/stores/ThreadChannelStore';
import { uiState } from '~/stores/UiState';
import { canOverlapInMainThread } from '~/utils/channelLogsUtil';
import { action, comparer, computed, observable } from 'mobx';
import { equals } from 'ramda';

class ThreadChannelLogsStore extends ChannelLogsStore {
  private mainThreadShowUserMap: StringTMap<StringTMap<boolean>> = {};

  private mainThreadShowSentAtMap: StringTMap<StringTMap<boolean>> = {};

  @observable
  dummyParentLogId: LOG_ID | null = null;

  @action
  setDummyParentLogId = (logId: LOG_ID | null) => {
    if (this.dummyParentLogId !== logId) {
      this.dummyParentLogId = logId;
    }
  };

  @observable
  logFilter: 'all' | 'unread' = 'all';

  @action
  setLogFilter = (filter: 'all' | 'unread') => {
    this.logFilter = filter;
  };

  ignoreThreadChannelCreate: LOG_ID = null;

  setIgnoreThreadChannelCreate = (parentLogId: LOG_ID | null) => {
    this.ignoreThreadChannelCreate = parentLogId;
  };

  @observable.shallow
  logIdListByThreadChannel: StringTMap<LOG_ID[]> = {};

  visibleStartIndex = -1;
  visibleStopIndex = -1;

  setVisibleIndex(startIndex, stopIndex) {
    this.visibleStartIndex = startIndex;
    this.visibleStopIndex = stopIndex;
  }

  get mainThreadParentChannelLogIdMap() {
    return Object.keys(this.logIdListByThreadChannel).reduce((acc: StringTMap<true>, channelId) => {
      const parentLogId = threadChannelStore.getById(channelId)?.parentChannelLogId;
      if (parentLogId) {
        acc[parentLogId] = true;
      }
      return acc;
    }, {});
  }

  @computed({ equals: comparer.shallow, keepAlive: true })
  get mainThreadChannelLogIdMap() {
    return Object.keys(this.logIdListByThreadChannel).reduce((acc: StringTMap<true>, channelId) => {
      this.logIdListByThreadChannel[channelId]?.forEach((logId) => {
        acc[logId] = true;
      });
      return acc;
    }, this.mainThreadParentChannelLogIdMap);
  }

  @computed({ equals: comparer.shallow, keepAlive: true })
  get usedLogIdMap() {
    return Object.assign(
      this.newLogId ? { [this.newLogId]: true } : {},
      this.threadPanelParentLogIdMap,
      this.logIdMap,
      this.dummyLogIdTokenMap,
      this.referenceLogIdMap,
      this.mainThreadChannelLogIdMap
    );
  }

  @computed({ equals: comparer.shallow, keepAlive: true })
  get threadPanelParentLogIdMap() {
    if (!this.threadPanelParentLogId) {
      return {};
    }
    if ((this.threadPanelParentLog as ReplyLog)?.originalLogId) {
      return {
        [this.threadPanelParentLogId]: true,
        [(this.threadPanelParentLog as ReplyLog)?.originalLogId]: true,
      };
    }
    return {
      [this.threadPanelParentLogId]: true,
    };
  }

  @computed
  get channel() {
    return threadChannelStore.getById(threadDetailUiState.contentsChannelId);
  }

  @computed
  get threadPanelParentLogId() {
    return threadChannelStore.threadLayerChannel?.parentChannelLogId ?? this.dummyParentLogId;
  }

  @computed
  get threadPanelParentLog(): Log | undefined {
    return this.getInner(this.threadPanelParentLogId);
  }

  @action
  public removeThreadLog = (log: Log) => {
    const logIds = this.logIdListByThreadChannel[log.channelId];
    const idx = logIds?.findIndex(equals(log.id));
    if (idx > -1) {
      this.logIdListByThreadChannel[log.channelId].splice(idx, 1);
      this.logIdListByThreadChannel[log.channelId] = this.logIdListByThreadChannel[log.channelId].slice();
    }
    this.removeLog(log.id);
  };

  @action
  public clearMainThreadChannelInfo = () => {
    this.logIdListByThreadChannel = {};
    this.mainThreadShowUserMap = {};
    this.mainThreadShowSentAtMap = {};
    this.gc();
  };

  public getParentLog = (logId: LOG_ID) => {
    if (this.mainThreadParentChannelLogIdMap[logId]) {
      return this.getInner(logId);
    }
  };

  public updateParentLog = (log: Log) => {
    const target = this.getInner(log.id);
    if (target) {
      channelLogData.setData(log.id, { ...target, ...log });
    }
  };

  public setParentLog = (log: Log, originalLog?: Log) => {
    channelLogData.setData(log.id, log);

    if (originalLog && !channelLogsStore.getById(originalLog.id)) {
      channelLogData.setData(originalLog.id, originalLog);
    }
  };

  public withDummyParentChannel = <T>(callback: (channel?: Channel) => T): T => {
    if (!this.dummyParentLogId || !(uiState.currentContext === 'thread' || threadDetailUiState.mainEditor.hasFocus())) {
      return callback();
    }
    return callback(channelsStore.getById(this.threadPanelParentLog.channelId));
  };

  @action
  public mergeMainThreadChannelLogs = (channelList: ThreadChannel[], channelLogMap: StringTMap<Log>) => {
    Object.keys(channelLogMap).forEach((logId) => {
      channelLogData.setData(logId, channelLogMap[logId]);
    });
    this.logIdListByThreadChannel = channelList.reduce((acc: StringTMap<LOG_ID[]>, channel) => {
      acc[channel.channelId] = channel.threadLogs;
      return acc;
    }, this.logIdListByThreadChannel);
  };

  public getMainThreadShowUser = (channelId: THREAD_CHANNEL_ID, logId: LOG_ID) => {
    return this.mainThreadShowUserMap[channelId]?.[logId] ?? false;
  };

  public getMainThreadShowSentAt = (channelId: THREAD_CHANNEL_ID, logId: LOG_ID) => {
    return this.mainThreadShowSentAtMap[channelId]?.[logId] ?? false;
  };

  public getThreadLogList = (channelId: THREAD_CHANNEL_ID) => {
    const logIdList = this.logIdListByThreadChannel[channelId];
    if (!logIdList || logIdList.length === 0) {
      return [];
    }
    const logList = logIdList.map(this.getInner);
    const { showUserMap, showSentAtMap } = this.calcVisibleProperty(logList, {
      showUser: true,
      showSentAt: true,
      canOverlapLogs: canOverlapInMainThread,
    });
    this.mainThreadShowUserMap[channelId] = showUserMap;
    this.mainThreadShowSentAtMap[channelId] = showSentAtMap;
    return logList;
  };

  public getReaction = (reactionId: REACTION_ID): Reaction => {
    return this.reactionMap[reactionId];
  };

  @action
  public addThreadLogList = (channelId: THREAD_CHANNEL_ID, log: Log) => {
    channelLogData.setData(log.id, log);
    this.logIdListByThreadChannel[channelId] = [...this.logIdListByThreadChannel[channelId], log.id];
  };

  public isThreadLog = (log: Log) => {
    return !!threadChannelStore.getById(log.channelId);
  };

  public updateLogInMainThreadChannel = (log: Log) => {
    if (this.mainThreadChannelLogIdMap[log.id]) {
      channelLogData.setData(log.id, { ...this.getInner(log.id), ...log });
    }
  };

  @computed({ equals: comparer.shallow })
  public get imageFileLogList(): LogFile[] {
    return this.filterImageFileLogList([this.threadPanelParentLog, ...this.logList]);
  }

  public getThreadImageLogList = (channelId: THREAD_CHANNEL_ID): LogFile[] => {
    const threadChannel = threadChannelStore.getById(channelId);
    const parentLog = this.getParentLog(threadChannel.parentChannelLogId);
    return this.filterImageFileLogList([parentLog, ...this.getThreadLogList(channelId)]);
  };
}

export const threadChannelLogsStore = new ThreadChannelLogsStore();
